import React, { useMemo, useState } from "react";
import {
  INDICATOR_CATEGORIES,
  INDICATOR_STATUS,
  searchIndicators,
} from "./indicatorRegistry";
import "./IndicatorLibraryPanel.css";

function IndicatorLibraryPanel({
  activeIndicatorIds = [],
  favoriteIndicatorIds = [],
  onToggleIndicator,
  onToggleFavorite,
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const activeSet = useMemo(() => new Set(activeIndicatorIds), [activeIndicatorIds]);
  const favoriteSet = useMemo(() => new Set(favoriteIndicatorIds), [favoriteIndicatorIds]);
  const results = useMemo(
    () => searchIndicators(query, { category, favorites: favoriteIndicatorIds }),
    [category, favoriteIndicatorIds, query]
  );

  return (
    <div className="indicatorLibraryPanel">
      <div className="indicatorLibrarySearchRow">
        <label>
          <span>Search indicators</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search VWAP, EMA, MACD, RSI…"
          />
        </label>
      </div>

      <nav className="indicatorLibraryCategories" aria-label="Indicator categories">
        <button
          type="button"
          className={category === "all" ? "active" : ""}
          onClick={() => setCategory("all")}
        >
          All
        </button>
        <button
          type="button"
          className={category === "favorites" ? "active" : ""}
          onClick={() => setCategory("favorites")}
        >
          Favorites
        </button>
        {INDICATOR_CATEGORIES.map((item) => (
          <button
            type="button"
            key={item}
            className={category === item ? "active" : ""}
            onClick={() => setCategory(item)}
          >
            {item}
          </button>
        ))}
      </nav>

      <div className="indicatorLibrarySummary" role="status">
        <span>{results.length} indicators</span>
        <span>{activeIndicatorIds.length} active</span>
      </div>

      <div className="indicatorLibraryResults">
        {results.length === 0 ? (
          <div className="indicatorLibraryEmpty">
            <strong>No matching indicators</strong>
            <p>Try another search term or category.</p>
          </div>
        ) : (
          results.map((indicator) => {
            const isActive = activeSet.has(indicator.id);
            const isFavorite = favoriteSet.has(indicator.id);
            const isAvailable = indicator.status === INDICATOR_STATUS.AVAILABLE;

            return (
              <article className="indicatorLibraryItem" key={indicator.id}>
                <div className="indicatorLibraryItemTopline">
                  <div>
                    <strong>{indicator.name}</strong>
                    <span>{indicator.category}</span>
                  </div>
                  <button
                    type="button"
                    className={`indicatorFavoriteButton${isFavorite ? " active" : ""}`}
                    aria-label={`${isFavorite ? "Remove" : "Add"} ${indicator.name} ${isFavorite ? "from" : "to"} favorites`}
                    aria-pressed={isFavorite}
                    onClick={() => onToggleFavorite?.(indicator.id)}
                  >
                    {isFavorite ? "★" : "☆"}
                  </button>
                </div>

                <p>{indicator.description}</p>

                <div className="indicatorLibraryMeta">
                  <span>{indicator.source}</span>
                  <span className={isAvailable ? "available" : "planned"}>
                    {isAvailable ? "Available" : "Planned · fail closed"}
                  </span>
                </div>

                <div className="indicatorLibraryRequirements">
                  <span>Requires:</span>
                  <span>{indicator.requirements.join(" · ")}</span>
                </div>

                <button
                  type="button"
                  className={`indicatorToggleButton${isActive ? " active" : ""}`}
                  disabled={!isAvailable}
                  aria-pressed={isActive}
                  onClick={() => onToggleIndicator?.(indicator.id)}
                >
                  {!isAvailable ? "Not connected" : isActive ? "Remove from chart" : "Add to chart"}
                </button>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

export default IndicatorLibraryPanel;
